import {
  createFlight,
  duplicateFlight,
  getFlight,
  getFlightAirports,
  getFlightCities,
  updateFlight,
} from '@/api/flight.api';
import { invalidateBenefitLists } from '@/hooks/useBenefits';
import { queryKeys } from '@/queryKeys';
import { CreateFlightWithTranslationsType } from '@/types/benefits/flight/CreateFlight.type';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useCreateFlight = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateFlightWithTranslationsType) =>
      createFlight(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flights.all });
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.citiesBase,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.airportsBase,
      });
    },
  });
};

export const useUpdateFlight = (language?: SupportedLocale) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      benefitId,
      data,
    }: {
      benefitId: string;
      data: Partial<CreateFlightWithTranslationsType>;
    }) => updateFlight(benefitId, data, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flights.all });
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.citiesBase,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.airportsBase,
      });
    },
  });
};

export const useGetFlightCities = (country?: string) =>
  useQuery({
    queryKey: queryKeys.flights.cities(country),
    queryFn: () => getFlightCities(country),
  });

export const useGetFlightAirports = (country?: string) =>
  useQuery({
    queryKey: queryKeys.flights.airports(country),
    queryFn: () => getFlightAirports(country),
  });

export const useGetFlight = (benefitId: string) =>
  useQuery({
    queryKey: queryKeys.flights.detail(benefitId),
    queryFn: () => getFlight(benefitId),
  });

export const useDuplicateFlight = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      benefitId,
      fields,
    }: {
      benefitId: string;
      fields: string[];
    }) => duplicateFlight(benefitId, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.flights.all });
      invalidateBenefitLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.citiesBase,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.flights.airportsBase,
      });
    },
  });
};
